<template>
  <div>
    <div class="page-header">
      <h2><span class="breadcrumb-level">会员管理</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level">会员信息</span><span class="breadcrumb-sep">/</span><span class="breadcrumb-level breadcrumb-current">小程序用户</span></h2>
      <div class="header-actions">
        <el-input v-model="searchQuery" placeholder="搜索姓名/手机号" clearable prefix-icon="Search" style="width:240px" @input="onSearchInput" />
      </div>
    </div>

    <el-table :data="users" stripe v-loading="loading" @sort-change="onSortChange">
      <el-table-column label="用户信息" min-width="180">
        <template #default="{row}">
          <div class="user-info-cell">
            <span class="user-name">{{row.name||'-'}}</span>
            <span class="user-phone">{{row.phone||'-'}}</span>
          </div>
        </template>
      </el-table-column>
      <el-table-column label="会员等级" width="130" align="center">
        <template #default="{row}">
          <el-tag size="small" effect="plain">{{row.member_level_name||'普通用户'}}</el-tag>
        </template>
      </el-table-column>
      <el-table-column label="账户余额" width="130" align="center" sortable="custom" prop="prepaid_balance">
        <template #default="{row}">
          <span style="color:#e6a23c;font-weight:600">¥{{ formatBalance(row.prepaid_balance) }}</span>
        </template>
      </el-table-column>
      <el-table-column label="注册时间" prop="join_time" width="110" align="center" sortable="custom">
        <template #default="{row}">{{formatDate(row.join_time)}}</template>
      </el-table-column>
      <el-table-column label="宠物档案" width="120" align="center">
        <template #default="{row}">
          <el-button size="small" type="warning" @click="goPetArchives(row)">
            查看 {{row.pet_count||0}}
          </el-button>
        </template>
      </el-table-column>
      <el-table-column label="入园登记" width="120" align="center">
        <template #default="{row}">
          <el-button size="small" type="info" @click="goPetEntry(row)">
            查看 {{row.entry_count||0}}
          </el-button>
        </template>
      </el-table-column>
      <el-table-column label="操作" width="240" align="center" fixed="right">
        <template #default="{row}">
          <el-button size="small" type="primary" @click="openChangeLevel(row)">变更会员</el-button>
          <el-button size="small" type="success" @click="openRecharge(row)">余额充值</el-button>
        </template>
      </el-table-column>
    </el-table>

    <div class="pagination-wrap" v-if="pagination.total > 0">
      <el-pagination
        v-model:current-page="pagination.current_page"
        :page-size="pagination.per_page"
        :total="pagination.total"
        layout="total, prev, pager, next"
        @current-change="onPageChange"
      />
    </div>

    <!-- 变更会员弹窗 -->
    <el-dialog v-model="levelDialogVisible" title="变更会员等级" width="420px">
      <el-form v-if="currentUser" label-width="100px">
        <el-form-item label="当前用户">
          <span>{{currentUser.name||'-'}}</span>
        </el-form-item>
        <el-form-item label="当前等级">
          <el-tag size="small">{{currentUser.member_level_name||'普通用户'}}</el-tag>
        </el-form-item>
        <el-form-item label="目标等级">
          <el-select v-model="targetLevel" placeholder="请选择等级" style="width:100%">
            <el-option label="普通用户（无等级）" :value="0" />
            <el-option v-for="lv in memberLevels" :key="lv.level" :label="lv.level_name" :value="lv.level" />
          </el-select>
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="levelDialogVisible=false">取消</el-button>
        <el-button type="primary" @click="handleChangeLevel" :loading="savingLevel">保存</el-button>
      </template>
    </el-dialog>

    <!-- 余额充值弹窗 -->
    <el-dialog v-model="rechargeDialogVisible" title="余额充值" width="420px">
      <el-form v-if="rechargeUser" label-width="100px">
        <el-form-item label="当前用户">
          <span>{{rechargeUser.name||'-'}}</span>
        </el-form-item>
        <el-form-item label="当前余额">
          <span style="color:#e6a23c;font-weight:600">¥{{ formatBalance(rechargeUser.prepaid_balance) }}</span>
        </el-form-item>
        <el-form-item label="充值金额">
          <el-input-number v-model="rechargeAmount" :min="1" :precision="2" style="width:100%" />
        </el-form-item>
        <el-form-item label="备注说明">
          <el-input v-model="rechargeRemark" type="textarea" :rows="2" placeholder="选填" />
        </el-form-item>
      </el-form>
      <template #footer>
        <el-button @click="rechargeDialogVisible=false">取消</el-button>
        <el-button type="success" @click="handleRecharge" :loading="savingRecharge">确认充值</el-button>
      </template>
    </el-dialog>
  </div>
</template>

<script setup>
import { ref, onMounted } from 'vue';
import { ElMessage } from 'element-plus';
import { Search } from '@element-plus/icons-vue';
import * as api from '../../api/userList';
import { getMemberLevels } from '../../api/userExtensions';
import { rechargeBalance } from '../../api/userList';

const users = ref([]);
const loading = ref(false);
const searchQuery = ref('');
let searchTimer = null;

const pagination = ref({
  current_page: 1,
  per_page: 10,
  total: 0,
  last_page: 1,
});

const sortBy = ref('id');
const sortOrder = ref('desc');

// 变更会员
const memberLevels = ref([]);
const levelDialogVisible = ref(false);
const currentUser = ref(null);
const targetLevel = ref(0);
const savingLevel = ref(false);

// 余额充值
const rechargeDialogVisible = ref(false);
const rechargeUser = ref(null);
const rechargeAmount = ref(100);
const rechargeRemark = ref('');
const savingRecharge = ref(false);

const formatDate = (v) => v ? v.substring(0, 10) : '-';
const formatBalance = (v) => (v ?? 0).toFixed(2);

const goPetArchives = (row) => {
  window.open('/pet-profile/archives?user_id=' + row.id, '_self');
};

const goPetEntry = (row) => {
  window.open('/pet-profile/entry?user_id=' + row.id, '_self');
};

// 变更会员
const openChangeLevel = (row) => {
  currentUser.value = row;
  targetLevel.value = row.member_level || 0;
  levelDialogVisible.value = true;
};

const handleChangeLevel = async () => {
  if (!currentUser.value) return;
  savingLevel.value = true;
  try {
    await api.updateMemberLevel(currentUser.value.id, targetLevel.value);
    ElMessage.success('会员等级已更新');
    levelDialogVisible.value = false;
    await fetchData();
  } catch { ElMessage.error('更新失败'); } finally { savingLevel.value = false; }
};

// 余额充值
const openRecharge = (row) => {
  rechargeUser.value = row;
  rechargeAmount.value = 100;
  rechargeRemark.value = '';
  rechargeDialogVisible.value = true;
};

const handleRecharge = async () => {
  if (!rechargeUser.value || !rechargeAmount.value) return;
  savingRecharge.value = true;
  try {
    await rechargeBalance(rechargeUser.value.id, rechargeAmount.value, rechargeRemark.value || '后台充值');
    ElMessage.success('充值成功');
    rechargeDialogVisible.value = false;
    // 更新当前行的余额
    rechargeUser.value.prepaid_balance = (parseFloat(rechargeUser.value.prepaid_balance || 0) + rechargeAmount.value);
    rechargeAmount.value = 100;
  } catch (e) {
    ElMessage.error(e?.response?.data?.message || '充值失败');
  } finally { savingRecharge.value = false; }
};

const fetchData = async () => {
  loading.value = true;
  try {
    const params = {
      page: pagination.value.current_page,
      per_page: pagination.value.per_page,
      sort_by: sortBy.value,
      sort_order: sortOrder.value,
    };
    if (searchQuery.value) params.search = searchQuery.value;
    const r = await api.getMemberUsers(params);
    const d = r.data || {};
    users.value = d.items || [];
    pagination.value = d.pagination || pagination.value;
  } catch { users.value = []; } finally { loading.value = false; }
};

const onSearchInput = () => {
  clearTimeout(searchTimer);
  pagination.value.current_page = 1;
  searchTimer = setTimeout(fetchData, 300);
};

const onPageChange = (page) => {
  pagination.value.current_page = page;
  fetchData();
};

const onSortChange = ({ prop, order }) => {
  if (prop) {
    sortBy.value = prop;
    sortOrder.value = order === 'ascending' ? 'asc' : 'desc';
  }
  fetchData();
};

onMounted(async () => {
  try {
    const r = await getMemberLevels();
    memberLevels.value = r.data || [];
  } catch {}
  await fetchData();
});
</script>

<style scoped>
.header-actions { display: flex; gap: 12px; align-items: center; }
.pagination-wrap { display: flex; justify-content: flex-end; margin-top: 16px; }
.user-info-cell { display: flex; flex-direction: column; gap: 4px; }
.user-name { font-weight: 500; font-size: 14px; color: #303133; }
.user-phone { font-size: 12px; color: #909399; }
</style>
